import { Button, FormField, Surface } from '@chopchop-se/ui';
import { useMutation } from '@tanstack/react-query';
import type { ReceivingScanResult } from '@warehouse/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { PageHeader } from '../../../components/PageHeader';
import { toastError, toastInfo, toastSuccess } from '../../../lib/notify';
import { receivingApi } from '../api';
import { ArticlePicker, SupplierPicker } from '../components/Pickers';
import { ScanField } from '../components/ScanField';
import { ScanResultCard } from '../components/ScanResultCard';
import type { ArticleOption } from '../master-data-port';

/**
 * `/warehouse/teach-barcodes` — teach-on-first-scan (spec §5.7, Victor Q16).
 *
 * Reached either from the receiving session's "Lär in" button (which passes
 * `?code=`) or directly by a supervisor pre-registering codes. Both land on the
 * same permission (`warehouse.barcodes.teach`) and the same audited command:
 * re-pointing a barcode changes it warehouse-wide, so it is never silent.
 */
export function TeachBarcodesPage(): ReactNode {
  const { t } = useTranslation('receiving');
  const [searchParams, setSearchParams] = useSearchParams();

  const [barcode, setBarcode] = useState(searchParams.get('code') ?? '');
  const [article, setArticle] = useState<ArticleOption | null>(null);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [qtyPerScan, setQtyPerScan] = useState('1');
  const [note, setNote] = useState('');
  const [scan, setScan] = useState<ReceivingScanResult | null>(null);

  const resolve = useMutation({
    mutationFn: (code: string) => receivingApi.scan({ code, supplierId }),
    onSuccess: (result) => {
      setScan(result);
      // Already known? Say so — teaching it again is a reassignment, not a new
      // mapping, and the operator should know before they save.
      if (result.article) {
        toastInfo(t('teach.existing', { article: result.article.name }));
      }
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const teach = useMutation({
    mutationFn: () =>
      receivingApi.teach({
        barcode: barcode.trim(),
        articleId: article!.id,
        supplierId,
        qtyPerScan,
        note: note.trim() || null,
      }),
    onSuccess: (result) => {
      toastSuccess(result.created ? t('teach.saved') : t('teach.reassigned'));
      setBarcode('');
      setScan(null);
      setNote('');
      setSearchParams({}, { replace: true });
    },
    onError: (error) => {
      toastError(error);
    },
  });

  // Arriving with ?code=… should immediately show what that code resolves to.
  useEffect(() => {
    const code = searchParams.get('code');
    if (code && !scan) resolve.mutate(code);
    // Only on mount / when the query param changes.
  }, [searchParams]);

  const ready = barcode.trim().length >= 3 && Boolean(article);

  return (
    <>
      <PageHeader title={t('teach.title')} subtitle={t('teach.subtitle')} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,32rem)_minmax(0,1fr)]">
        <Surface className="space-y-4 p-4">
          <ScanField
            label={t('teach.barcode')}
            placeholder={t('teach.barcodePlaceholder')}
            onScan={(code) => {
              setBarcode(code);
              resolve.mutate(code);
            }}
            busy={resolve.isPending}
            testId="teach-scan"
          />

          <FormField
            label={t('teach.barcode')}
            value={barcode}
            onChange={(event) => {
              setBarcode(event.target.value);
            }}
            {...(barcode.length > 0 && barcode.trim().length < 3
              ? { error: t('teach.barcodeRequired') }
              : {})}
          />

          <ArticlePicker label={t('teach.article')} value={article?.id ?? null} onChange={setArticle} />

          <SupplierPicker
            label={t('teach.supplier')}
            value={supplierId}
            onChange={(supplier) => {
              setSupplierId(supplier?.id ?? null);
            }}
          />
          <p className="-mt-2 text-xs text-ink-muted">{t('teach.supplierHint')}</p>

          <FormField
            label={t('teach.qtyPerScan')}
            type="number"
            min="0.001"
            step="0.001"
            inputMode="decimal"
            value={qtyPerScan}
            onChange={(event) => {
              setQtyPerScan(event.target.value);
            }}
          />

          <FormField
            label={t('teach.note')}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />

          <Button
            className="w-full"
            disabled={!ready}
            loading={teach.isPending}
            onClick={() => {
              teach.mutate();
            }}
          >
            {t('teach.submit')}
          </Button>
        </Surface>

        <div>{scan ? <ScanResultCard scan={scan} /> : null}</div>
      </div>
    </>
  );
}
